import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IAssetFromCollection } from '../../../../../components/shared/major/photos-collection/photos-list/photo-list-model';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ConfigurationService } from '../../../../../services/configuration.service';
import {
    AssetsMainManagerService,
    IAssetMainService,
    IGenerateAssetFunctionParams,
    IGeneratedAssetResponse,
} from './assets-main-manager.service';
import { SnackbarActionEnum, SnackBarService } from '../../../../../services/utils/snack-bar.service';
import { catchErrorTypedAsync } from '../../../../../services/functions-helper.service';
import { IUpdateTakeImage } from '../../../../../services/http-models/project/take/take-http-request-model';
import { AssetType } from 'src/app/models/defines';
import {
    IExtendVideoGeneration,
    IGenerateVideoFromAsset,
    IGenerateVideoFromSimpleText,
    IGenerateVideoRequest,
} from '../../../../../services/http-models/asset/asset-http-request-model';
import { IGeneratedVideoSocketData } from '../../../../../models/socket-events/workspace/asset/workspace-asset-event';
import { FeatureService } from '../../../../../services/feature-service.service';
import { IUpdateVideoLayerByAsset } from '../../../../../services/http-models/project/scene/scene-http-request-model';
import { IComponentUpdateWithAsset } from '../assets.component';
import {
    ASPECT_RATIO_ENUM,
    IGenerateAssetFromReference,
    IGenerateAssetFromText,
    IMAGE_GENERATION_MODELS_ENUM,
} from '../../../../../models/assets-model';
import { ILocalAsset } from '../../../../../models/assets/assets.model';
import { AssetNavigationService } from '../state/asset-navigation.service';
import { AssetSelectionService } from '../state/asset-selection.service';

export type ComponentId =
    | 'photo-library'
    | 'video-library'
    | 'generate-image'
    | 'file-drop'
    | 'my-assets'
    | 'generate-video';

interface IButtonToNavigate {
    id: ComponentId;
    text: string;
    icon: string;
    display: boolean;
    disabled: boolean;
}

interface IComponents {
    photoLibrary: ComponentId;
    videoLibrary: ComponentId;
    generateImage: ComponentId;
    generateVideo: ComponentId;
    fileDrop: ComponentId;
    myAssets: ComponentId;
}

@Component({
    selector: 'app-assets-main',
    templateUrl: './assets-main.component.html',
    styleUrls: ['./assets-main.component.scss'],
    providers: [AssetNavigationService, AssetSelectionService],
})
export class AssetsMainComponent implements OnInit, OnDestroy {
    @Input() projectId: string | null = null;
    assetsMainConfigs: IAssetMainService;
    theFirstAssetsConfig: IAssetMainService;
    buttons: IButtonToNavigate[] = [
        {
            id: 'photo-library',
            text: 'Stock Images',
            icon: 'image',
            display: true,
            disabled: false,
        },
        {
            id: 'video-library',
            text: 'Stock Videos',
            icon: 'video_library',
            display: true,
            disabled: false,
        },
        {
            id: 'file-drop',
            text: 'Upload',
            icon: 'cloud_upload',
            display: true,
            disabled: false,
        },
        {
            id: 'generate-image',
            text: 'Generate image',
            icon: 'imagesmode',
            display: true,
            disabled: false,
        },
        {
            id: 'generate-video',
            text: 'Generate video',
            icon: 'movie_filter',
            display: true,
            disabled: false,
        },
    ];
    returnButton: IButtonToNavigate = {
        id: 'my-assets',
        text: '',
        icon: '',
        display: true,
        disabled: false,
    };
    components: IComponents = {
        photoLibrary: 'photo-library',
        videoLibrary: 'video-library',
        generateImage: 'generate-image',
        fileDrop: 'file-drop',
        myAssets: 'my-assets',
        generateVideo: 'generate-video',
    };
    activeComponentId: ComponentId = 'my-assets';
    isLoadingImageSwap: boolean;
    isLoadingAssetGenerate: boolean;
    isLoadingVideoGenerateRequest: boolean;
    isUploadingImage: boolean;
    generatedImageSource: IGeneratedAssetResponse;
    baseCdnUrl: string;
    toDisplayHeader: boolean;
    toMakeUploadAsMainButton: boolean;
    AssetType = AssetType;
    headlineText: string = 'My Assets';
    enableToEditImage: boolean = false;
    enableImageToVideo: boolean = false;
    componentConfig: Record<
        ComponentId,
        {
            headlineText: string;
            toDisplayHeader?: boolean;
            toDisplayNavigationButtons: boolean;
            toDisplayBackButton: boolean;
        }
    > = {
        'generate-image': {
            headlineText: 'Generate Image',
            toDisplayHeader: true,
            toDisplayNavigationButtons: false,
            toDisplayBackButton: true,
        },
        'file-drop': { headlineText: 'Upload Image', toDisplayNavigationButtons: false, toDisplayBackButton: true },
        'my-assets': { headlineText: 'Image Library', toDisplayNavigationButtons: true, toDisplayBackButton: false },
        'photo-library': {
            headlineText: 'Stock Library',
            toDisplayNavigationButtons: false,
            toDisplayBackButton: true,
        },
        'generate-video': {
            headlineText: 'Generate Video',
            toDisplayNavigationButtons: false,
            toDisplayBackButton: true,
        },
        'video-library': {
            headlineText: 'Video Stock',
            toDisplayNavigationButtons: false,
            toDisplayBackButton: true,
        },
    };
    toDisplayNavigationButtons: boolean;
    toDisplayBackButton: boolean;
    assetTypeDisplay: AssetType;
    assetTypesToAccept: string[] = [];
    acceptAttr: string = '';
    isDraggingOverUpload = false;
    private dragCounter = 0;
    onDestroy$ = new Subject<boolean>();
    toDisplaySelectAsset = true;
    toDisplayExitButton = true;
    toDisplayButtons = true;
    toDisplayUseMeInVideoGenerating = false;
    toDisplayUseMeInImageGenerating = false;
    isHosted: boolean;
    toDisplayLoading: boolean;
    generatedVideoSocketData: IGeneratedVideoSocketData;
    hideGenerateVideoButton = false;
    selectAssetImmediate: ILocalAsset;
    private assetTypeAcceptMap: Record<AssetType, string[]> = {
        [AssetType.VIDEO]: ['.mp4'],
        [AssetType.IMAGE]: ['.svg', '.png', '.jpeg', '.jpg', '.gif'],
        [AssetType.PDF]: ['.pdf'],
        [AssetType.Audio]: ['.mp3', '.wav', '.aac'],
        [AssetType.FOLDER]: [],
    };
    private generatedVideoSubscription: Subscription;

    constructor(
        private configurationService: ConfigurationService,
        private assetsMainManager: AssetsMainManagerService,
        private snackBarService: SnackBarService,
        private featureService: FeatureService
    ) {
        this.baseCdnUrl = this.configurationService.baseCdnUrl;
        this.setActiveComponent('my-assets');
    }

    ngOnInit(): void {
        this.assetsMainManager.assetMainConfigs$.pipe(takeUntil(this.onDestroy$)).subscribe((assetMainConfigs) => {
            if (!this.theFirstAssetsConfig) {
                this.theFirstAssetsConfig = assetMainConfigs;
            }
            this.assetsMainConfigs = assetMainConfigs;
            this.toDisplayHeader = this.assetsMainConfigs.toDisplayHeader;
            this.toDisplaySelectAsset = assetMainConfigs.toDisplaySelectAsset;
            this.toDisplayExitButton = assetMainConfigs.toDisplayExitButton;
            this.toMakeUploadAsMainButton = assetMainConfigs.toMakeUploadAsMainButton;
            this.isHosted = assetMainConfigs.isHosted;
            this.toDisplayLoading = assetMainConfigs.toDisplayLoading;
            this.enableImageToVideo = assetMainConfigs.toEnableImageToVideo;
            this.enableToEditImage = assetMainConfigs.toEnableEditImage;
            this.updateButtonDisplay();
            if (typeof assetMainConfigs.toDisplayUseMeButtonWithVideoGenerating === 'boolean') {
                this.toDisplayUseMeInVideoGenerating = assetMainConfigs.toDisplayUseMeButtonWithVideoGenerating;
            }
            if (typeof assetMainConfigs.toDisplayUseMeButtonWithImageGenerating === 'boolean') {
                this.toDisplayUseMeInImageGenerating = assetMainConfigs.toDisplayUseMeButtonWithImageGenerating;
            }

            if (this.generatedVideoSubscription) {
                this.generatedVideoSubscription.unsubscribe();
            }
            this.generatedVideoSubscription = assetMainConfigs.generatedVideo$
                ? assetMainConfigs.generatedVideo$.pipe(takeUntil(this.onDestroy$)).subscribe((data) => {
                      if (!data) {
                          return;
                      }
                      this.isLoadingAssetGenerate = false;
                      this.generatedVideoSocketData = null;
                      this.generatedVideoSocketData = data;
                  })
                : null;
        });
        this.getGenerateVideoButtonFlagAsync();
    }

    navigateButtonClicked(button: IButtonToNavigate) {
        if (!button) {
            return;
        }
        this.setActiveComponent(button.id);
    }

    async selectNewAssetAsync(updateImage: IUpdateTakeImage) {
        this.isLoadingImageSwap = true;
        const { data, error } = await catchErrorTypedAsync(this.assetsMainConfigs.createNewAssetAsync(updateImage));
        this.isLoadingImageSwap = false;
        if (!data) {
            return;
        }
        this.onAssetChosen();
    }

    async selectVideoToUpdateAsync(videoData: IUpdateVideoLayerByAsset) {
        this.isLoadingImageSwap = true;
        if (videoData.type === 'assetLibrary') {
            this.selectImmediateAssetAndNavigateBack(videoData.asset);
            return;
        }
        const { data, error } = await catchErrorTypedAsync(this.assetsMainConfigs.useGeneratedAssetAsync(videoData));
        this.isLoadingImageSwap = false;
        if (!data) {
            return;
        }
        this.onAssetChosen();
    }

    private selectImmediateAssetAndNavigateBack(asset: ILocalAsset) {
        this.selectAssetImmediate = asset;
        //// Doing that will trigger the select asset from the assets component which will show loading
        this.navigateButtonClicked(this.returnButton);
    }

    async onPhotoSelectedFromPhotoList(payload: {
        assetFromCollection: IAssetFromCollection;
        type: 'image' | 'video';
    }) {
        if (!payload?.assetFromCollection) {
            return;
        }
        const { type, assetFromCollection } = payload;
        if (type === 'image') {
            this.selectNewAssetAsync({
                pickImageFromUnsplash: {
                    photoId: assetFromCollection.id,
                },
            });
        } else if (type === 'video') {
            this.selectVideoToUpdateAsync({
                type: 'pexels',
                videoId: assetFromCollection.id,
            });
        }
    }

    async selectMyAsset(assetData: ILocalAsset) {
        if (!assetData) {
            return;
        }
        this.selectNewAssetAsync({
            pickAsset: {
                imageRelativePath: assetData.uploadPath,
                imageId: assetData._id,
                asset: assetData,
            },
        });
    }

    async generateAsset(event: {
        startFromAsset: IGenerateAssetFromReference | null;
        startFromSimpleText: IGenerateAssetFromText | null;
    }) {
        this.isLoadingAssetGenerate = true;
        const { startFromAsset, startFromSimpleText } = event;
        let prompt: string;
        let assetId: string | undefined;
        let model: IMAGE_GENERATION_MODELS_ENUM;
        let aspectRatio: ASPECT_RATIO_ENUM;
        let toUseLogo: boolean;
        if (startFromAsset) {
            prompt = startFromAsset.enrichWithText;
            assetId = startFromAsset.assetId;
            model = startFromAsset.model;
            aspectRatio = startFromAsset.aspectRatio;
            toUseLogo = startFromAsset.toUseLogo;
        } else if (startFromSimpleText) {
            prompt = startFromSimpleText.text;
            model = startFromSimpleText.model;
            aspectRatio = startFromSimpleText.aspectRatio;
            toUseLogo = startFromSimpleText.toUseLogo;
        } else {
            this.isLoadingAssetGenerate = false;
            return;
        }

        const generateAssetParams: IGenerateAssetFunctionParams = {
            projectId: this.projectId,
            prompt: prompt,
            assetId: assetId,
            model: model,
            aspectRatio: aspectRatio,
            toUseLogo: toUseLogo,
        };

        const { data, error } = await catchErrorTypedAsync(
            this.assetsMainConfigs.generateAssetAsync(generateAssetParams)
        );
        if (data) {
            this.generatedImageSource = data;
        }
        this.isLoadingAssetGenerate = false;
    }

    async editImageAsync(event: { assetId: string }) {
        // TODO: Implement image editing functionality
        console.log('Edit image with assetId:', event.assetId);
        // This will open an image editor or navigate to edit page
    }

    async generateVideoAsync(event: {
        startFromAsset: IGenerateVideoFromAsset;
        startFromSimpleText: IGenerateVideoFromSimpleText;
        extendVideo: IExtendVideoGeneration;
    }) {
        const { startFromAsset, startFromSimpleText, extendVideo } = event;
        const dataForGeneratingVideo: IGenerateVideoRequest = {
            projectId: this.projectId,
            toStartFromAsset: startFromAsset,
            startFromSimpleText: startFromSimpleText,
            extendVideo: extendVideo,
            folderId: null, //// The folder is being set after in the asset library service
            model: extendVideo ? null : startFromAsset?.model ? startFromAsset.model : startFromSimpleText.model,

            needToChangeAspectRatio: startFromAsset?.needToChangeAspectRatio,
            aspectRatio: extendVideo
                ? null
                : startFromAsset?.aspectRatio
                  ? startFromAsset?.aspectRatio
                  : startFromSimpleText.aspectRatio,
        };
        this.isLoadingAssetGenerate = true;
        this.generatedVideoSocketData = null;
        const { data, error } = await catchErrorTypedAsync(
            this.assetsMainConfigs.generateVideoAsync(dataForGeneratingVideo)
        );
        this.isLoadingAssetGenerate = false;
    }

    async useGeneratedVideoAsync(data: { asset: ILocalAsset }) {
        if (!data?.asset) {
            return;
        }
        this.selectVideoToUpdateAsync({
            type: 'assetLibrary',
            asset: data.asset,
        });
    }

    async useGeneratedImageAsync(data: { asset: ILocalAsset }) {
        if (!data?.asset) {
            return;
        }
        this.selectImmediateAssetAndNavigateBack(data.asset);
        return;
    }

    async onFileDrop(formData: FormData) {
        this.isUploadingImage = true;
        const { data, error } = await this.assetsMainConfigs.onFileDrop(formData);
        this.isUploadingImage = false;
        if (error) {
            console.error(`Error picking photo.`, error);
            this.snackBarService.openMessage(
                `We couldn't upload this image. Please upload another one :)`,
                SnackbarActionEnum.Close,
                5000
            );
        }
        this.assetsMainConfigs.callbacks?.onFileUploaded();
        if (this.assetsMainConfigs.callbacks?.useBackArrowWhenImageSelected) {
            this.navigateButtonClicked(this.returnButton);
        }
    }

    setActiveComponent(id: ComponentId) {
        this.activeComponentId = id;

        let config = this.componentConfig[id];
        if (!config) {
            config = this.componentConfig['my-assets'];
        }
        if (this.activeComponentId === 'my-assets' && this.theFirstAssetsConfig) {
            this.assetsMainManager.setAssetMainConfigs(this.theFirstAssetsConfig);
        }
        const { toDisplayNavigationButtons, toDisplayBackButton, headlineText } = config;
        this.headlineText = headlineText;
        this.toDisplayNavigationButtons = toDisplayNavigationButtons;
        this.toDisplayBackButton = toDisplayBackButton;
    }

    exitButtonClicked() {
        this.assetsMainConfigs.callbacks?.onCloseButtonClicked();
    }

    onAssetChosen() {
        this.assetsMainConfigs.callbacks?.onAssetChosen();
        if (this.assetsMainConfigs.callbacks?.useBackArrowWhenImageSelected) {
            this.navigateButtonClicked(this.returnButton);
        }
    }

    onFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            return;
        }

        this.uploadFilesList(input.files);

        // Reset so picking the same file(s) again still fires 'change'
        input.value = '';
    }

    handleFileDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.dragCounter = 0;
        this.isDraggingOverUpload = false;

        const files = event.dataTransfer?.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadFilesList(files);
    }

    /** Builds a single batched FormData ('files[]') from all selected/dropped files and uploads them. */
    private uploadFilesList(files: FileList): void {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('files[]', files[i]);
        }

        this.assetsMainConfigs.onFileDrop(formData);
    }

    onDragEnter(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.dragCounter++;
        this.isDraggingOverUpload = true;
    }

    onDragOver(event: DragEvent): void {
        // Required so the browser allows the drop to fire
        event.preventDefault();
        event.stopPropagation();
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.dragCounter--;
        if (this.dragCounter <= 0) {
            this.dragCounter = 0;
            this.isDraggingOverUpload = false;
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next(true);
        this.onDestroy$.complete();
    }

    updateComponent(data: IComponentUpdateWithAsset) {
        this.setActiveComponent(data.component);
        this.generatedImageSource = {
            assetId: data.asset._id,
            asset: data.asset,
            uploadPath: data.asset.uploadPath,
        };
    }

    extendVideoAsync(asset: ILocalAsset) {
        this.generateVideoAsync({
            extendVideo: {
                assetId: asset._id,
                enrichVideoWithText: '',
            },
            startFromSimpleText: null,
            startFromAsset: null,
        });
    }

    resetAssetData() {
        this.generatedImageSource = null;
        this.generatedVideoSocketData = null;
    }

    private async getGenerateVideoButtonFlagAsync() {
        this.hideGenerateVideoButton = await this.featureService.getFeatureAccessValue('generate_video_hidden');
        this.buttons.forEach((button) => {
            if (button.id === 'generate-video') {
                button.disabled = this.hideGenerateVideoButton;
            }
        });
    }

    private updateButtonDisplay(): void {
        const titleMapping: Record<ComponentId, boolean> = {
            'my-assets': true,
            'photo-library': this.assetsMainConfigs.displayTitles.library,
            'video-library': this.assetsMainConfigs.displayTitles.videoLibrary,
            'generate-image': this.assetsMainConfigs.displayTitles.generate,
            'file-drop': this.assetsMainConfigs.displayTitles.upload,
            'generate-video': this.assetsMainConfigs.displayTitles.generateVideo,
        };
        this.buttons = this.buttons.map((button) => ({
            ...button,
            display: titleMapping[button.id] ?? button.display,
        }));
        switch (this.assetsMainConfigs.asset) {
            case 'all':
                this.assetTypeDisplay = null;
                this.assetTypesToAccept = [
                    ...this.assetTypeAcceptMap[AssetType.VIDEO],
                    ...this.assetTypeAcceptMap[AssetType.IMAGE],
                    ...this.assetTypeAcceptMap[AssetType.PDF],
                ];
                break;

            case 'video':
                this.assetTypeDisplay = AssetType.VIDEO;
                this.assetTypesToAccept = this.assetTypeAcceptMap[AssetType.VIDEO];
                break;

            case 'image':
                this.assetTypeDisplay = AssetType.IMAGE;
                this.assetTypesToAccept = this.assetTypeAcceptMap[AssetType.IMAGE];
                break;

            case 'pdf':
                this.assetTypeDisplay = AssetType.PDF;
                this.assetTypesToAccept = this.assetTypeAcceptMap[AssetType.PDF];
                break;
        }
        this.acceptAttr = this.assetTypesToAccept.join(',');
    }
}
